import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Cookie, X, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import { safeStorage } from '../../utils/safeStorage';

interface CookiesAlertProps {
  className?: string;
}

const CookiesAlert: React.FC<CookiesAlertProps> = ({ className = '' }) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Check if user has already accepted cookies
    const hasAcceptedCookies = safeStorage.getItem('cookiesAccepted');
    if (!hasAcceptedCookies) {
      // Show the alert after a longer delay for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
        setIsAnimating(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    safeStorage.setItem('cookiesAccepted', 'all');
    safeStorage.setItem('cookiesAcceptedDate', new Date().toISOString());
    hideAlert();
  };

  const handleAcceptEssential = () => {
    safeStorage.setItem('cookiesAccepted', 'essential');
    safeStorage.setItem('cookiesAcceptedDate', new Date().toISOString());
    hideAlert();
  };

  const handleDecline = () => {
    safeStorage.setItem('cookiesAccepted', 'declined');
    safeStorage.setItem('cookiesAcceptedDate', new Date().toISOString());
    hideAlert();
  };

  const hideAlert = () => {
    setIsAnimating(false);
    const timer = setTimeout(() => setIsVisible(false), 300);
    // Store timer ref for potential cleanup
    return () => clearTimeout(timer);
  };

  if (!isVisible) return null;

  return (
    <div className={`fixed bottom-4 left-4 right-4 z-[150] ${className}`}>
      <div
        className={`bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-gray-200 transition-all duration-300 max-w-md mx-auto sm:ml-auto sm:mr-0 ${isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
          }`}
      >
        <div className="p-4 sm:p-6">
          <div className="flex items-start gap-3 sm:gap-4">
            <div className="flex-shrink-0">
              <Cookie className="w-5 h-5 sm:w-6 sm:h-6 text-primary-500 mt-1" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">
                {t('cookies.title')}
              </h3>
              <p className="text-gray-600 text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4">
                {t('cookies.description')}
              </p>

              <div className="flex flex-col gap-2">
                <button
                  onClick={handleAcceptAll}
                  className="w-full px-3 sm:px-4 py-2 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  {t('cookies.actions.acceptAll')}
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={handleAcceptEssential}
                    className="flex-1 px-2 sm:px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg text-xs sm:text-sm font-medium transition-colors duration-200"
                  >
                    {t('cookies.actions.essential')}
                  </button>
                  <button
                    onClick={handleDecline}
                    className="flex-1 px-2 sm:px-3 py-2 text-gray-600 hover:text-gray-800 text-xs sm:text-sm font-medium transition-colors duration-200"
                  >
                    {t('cookies.actions.decline')}
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 mt-3 text-xs text-gray-500">
                <Link
                  to="/terms"
                  className="hover:text-primary-500 underline transition-colors"
                >
                  {t('cookies.links.terms')}
                </Link>
                <span>•</span>
                <Link
                  to="/privacy"
                  className="hover:text-primary-500 underline transition-colors"
                >
                  {t('cookies.links.privacy')}
                </Link>
                <span>•</span>
                <Link
                  to="/cookie-settings"
                  className="hover:text-primary-500 underline transition-colors"
                >
                  {t('cookies.links.cookieSettings')}
                </Link>
              </div>
            </div>

            <button
              onClick={hideAlert}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors p-1"
              aria-label={t('common.close')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiesAlert;
